import * as React from "react";

import {
  Button as SaltButton,
  type ButtonAppearance,
  type ButtonProps as SaltButtonProps,
  type ButtonSentiment,
} from "@/design-system/salt";

/**
 * Product-level button intents mapped onto Salt's appearance and sentiment.
 * `primary` is the explicit accented page action; the historical `default`
 * remains the neutral solid action.
 */
export type ButtonVariant =
  | "default"
  | "primary"
  | "outline"
  | "secondary"
  | "ghost"
  | "destructive";

const variantToSaltProps: Record<
  ButtonVariant,
  { appearance: ButtonAppearance; sentiment: ButtonSentiment }
> = {
  default: { appearance: "solid", sentiment: "neutral" },
  primary: { appearance: "solid", sentiment: "accented" },
  outline: { appearance: "bordered", sentiment: "neutral" },
  secondary: { appearance: "transparent", sentiment: "neutral" },
  ghost: { appearance: "transparent", sentiment: "neutral" },
  destructive: { appearance: "solid", sentiment: "negative" },
};

export interface ButtonProps
  extends Omit<SaltButtonProps, "appearance" | "sentiment" | "variant"> {
  variant?: ButtonVariant;
  style?: React.CSSProperties;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { children, className, loading, loadingAnnouncement, type, variant = "default", ...props },
    ref,
  ) => {
    const { appearance, sentiment } = variantToSaltProps[variant];
    return (
      <SaltButton
        {...props}
        appearance={appearance}
        className={className}
        loading={loading}
        loadingAnnouncement={loading ? loadingAnnouncement : undefined}
        ref={ref}
        sentiment={sentiment}
        type={type ?? "submit"}
      >
        {children}
      </SaltButton>
    );
  },
);
Button.displayName = "Button";

/**
 * Compound-control variant helper retained as a plain function returning the
 * Salt class string. `calendar.tsx` feeds the result to `react-day-picker`
 * slot `classNames`. Do NOT remove until calendar.tsx is independently migrated.
 *
 * The string MUST lead with the base `saltButton` class. Every paint
 * declaration Salt ships for a button — background, border, radius, colour,
 * display, gap, font, height, padding — lives on the `.saltButton` rule
 * (`@salt-ds/core/css/salt-core.css:375-405`). The appearance and sentiment
 * rules (`.saltButton-neutral.saltButton-solid` etc.) set only `--button-*`
 * custom properties, which nothing reads without `.saltButton`. Emitting the
 * modifiers alone type-checks, reads as correct, and paints nothing.
 *
 * `saltButton-primary` is Salt's own default `variant`, which
 * `<Button>` never overrides — including it keeps this helper's output
 * identical to what rendering `<Button variant={…}>` actually produces.
 * `button.test.tsx` asserts that equality so the two cannot drift.
 */
export function buttonVariants(opts: { variant?: ButtonVariant } = {}): string {
  const { appearance, sentiment } = variantToSaltProps[opts.variant ?? "default"];
  return `saltButton saltButton-primary saltButton-${appearance} saltButton-${sentiment}`;
}

export { Button };
